import { useState, useEffect } from 'react';
import {
  Modal, ModalDialog, ModalClose, DialogTitle, DialogContent, DialogActions,
  FormControl, FormLabel, Input, Textarea, Select, Option, Button, Stack,
  Checkbox, Typography,
} from '@mui/joy';
import {
  Project, ProjectStatus, CreateProjectRequest, UpdateProjectRequest,
  PROJECT_STATUS_LABELS,
} from '@/types/project';

interface ProjectFormProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: CreateProjectRequest | UpdateProjectRequest) => Promise<void>;
  project?: Project | null;
}

const ALL_STATUSES: ProjectStatus[] = ['not_started', 'in_progress', 'completed', 'on_hold', 'cancelled'];

export default function ProjectForm({ open, onClose, onSubmit, project }: ProjectFormProps) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [status, setStatus] = useState<ProjectStatus>('not_started');
  const [isPrivate, setIsPrivate] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (project) {
      setName(project.name);
      setDescription(project.description || '');
      setStatus(project.status);
      setIsPrivate(project.is_private ?? false);
      setStartDate(project.start_date || '');
      setEndDate(project.end_date || '');
    } else {
      setName('');
      setDescription('');
      setStatus('not_started');
      setIsPrivate(false);
      setStartDate('');
      setEndDate('');
    }
  }, [project, open]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      await onSubmit({
        name,
        description: description || undefined,
        status,
        is_private: isPrivate,
        start_date: startDate || undefined,
        end_date: endDate || undefined,
      });
      onClose();
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose}>
      <ModalDialog sx={{ maxWidth: 500, width: '100%' }}>
        <ModalClose />
        <DialogTitle>{project ? 'Edit Project' : 'New Project'}</DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <Stack spacing={2}>
              <FormControl required>
                <FormLabel>Name</FormLabel>
                <Input value={name} onChange={e => setName(e.target.value)} autoFocus />
              </FormControl>
              <FormControl>
                <FormLabel>Description</FormLabel>
                <Textarea minRows={2} value={description} onChange={e => setDescription(e.target.value)} />
              </FormControl>
              <FormControl>
                <FormLabel>Status</FormLabel>
                <Select value={status} onChange={(_, v) => v && setStatus(v)}>
                  {ALL_STATUSES.map(s => (
                    <Option key={s} value={s}>{PROJECT_STATUS_LABELS[s]}</Option>
                  ))}
                </Select>
              </FormControl>
              <Stack direction="row" spacing={2}>
                <FormControl sx={{ flex: 1 }}>
                  <FormLabel>Start Date</FormLabel>
                  <Input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} />
                </FormControl>
                <FormControl sx={{ flex: 1 }}>
                  <FormLabel>End Date</FormLabel>
                  <Input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} />
                </FormControl>
              </Stack>
              <Checkbox
                checked={isPrivate}
                onChange={e => setIsPrivate(e.target.checked)}
                label={
                  <Stack>
                    <Typography level="body-sm">Private</Typography>
                    <Typography level="body-xs" color="neutral">Only visible to you and assigned members</Typography>
                  </Stack>
                }
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button variant="plain" color="neutral" onClick={onClose}>Cancel</Button>
            <Button type="submit" loading={loading} disabled={!name.trim()}>
              {project ? 'Save' : 'Create'}
            </Button>
          </DialogActions>
        </form>
      </ModalDialog>
    </Modal>
  );
}
